/**
Author - Pradip Dhakal
Git - https://github.com/pradipwealthy
**/

import React, {useState} from 'react';
import Dropzone from 'react-dropzone';
import parse from 'html-react-parser';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {ActionButton, Dropdown} from 'screens/commons';
import IMAGES from 'constants/Images';
import {SectionHeaderContainer} from 'components/common/StyledComponents';

/*
NOTE: Description of the props
commonInstructions: any instructions including html tags
actionButton: {
    buttonText: "text of the button",
    callback: "function to call when the button is clicked",
    isLoading: "boolean to show loading state",
    isDisabled: "boolean to disable the button"
}
documents: [
    {
        "heading": "heading of the document",
        "subHeading": "sub heading of the document",
        "documentOptions": allowed options for document,
        "selectedOption": selected document option, 
        "onChangeDocOption": callback fn for handling the selected option, 
        "uploadMessage": "message to show when the document is uploaded",
        "multiple": "boolean to allow multiple files",
        "name": "name of the document",
        "onDrop": "function to call when the document is dropped",
        "group": "group of the document" <- based on the group, the document will be shown in the same group
    }
]
handleDeleteUploadedFile - function to handle delete uploaded file
uploadedFiles - uploaded files
acceptFiles - supported file types format: {
    "mimeType": [".extension"],
}
*/
const propTypes = {
  commonInstructions: PropTypes.string,
  actionButton: PropTypes.object,
  documents: PropTypes.arrayOf(
    PropTypes.shape({
      heading: PropTypes.string.isRequired,
      subHeading: PropTypes.string.isRequired,
      documentOptions: PropTypes.arrayOf(
        PropTypes.shape({
          key: PropTypes.string.isRequired,
          text: PropTypes.string.isRequired,
          render: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
        })
      ),
      selectedOption: PropTypes.shape({
        key: PropTypes.string.isRequired,
        text: PropTypes.string.isRequired,
        render: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
      }),
      onChangeDocOption: PropTypes.func,
      uploadMessage: PropTypes.string.isRequired,
      multiple: PropTypes.bool.isRequired,
      name: PropTypes.string.isRequired,
      onDrop: PropTypes.func.isRequired,
      group: PropTypes.string.isRequired,
    })
  ).isRequired,
  acceptFiles: PropTypes.object,
};

const UploadMultipleDocuments = ({
  commonInstructions = '',
  actionButton = {
    buttonText: 'Upload & Continue',
    callback: () => {},
    isLoading: false,
    isDisabled: false,
  },
  documents = [],
  handleDeleteUploadedFile = () => {},
  uploadedFiles = [],
  acceptFiles = {
    'image/png': ['.png'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'application/pdf': ['.pdf'],
  },
}) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const getOptions = (documentOptions, onChangeSelectedOption) =>
    documentOptions?.map((eachDeclaration) => (
      <EachOption
        key={eachDeclaration.key}
        onClick={() => {
          onChangeSelectedOption(eachDeclaration);
        }}
      >
        {parse(eachDeclaration?.render)}
      </EachOption>
    ));

  const groupedDocuments = documents.reduce((acc, doc) => {
    const group = doc.group;
    acc[group] = acc[group] || [];
    acc[group].push(doc);
    return acc;
  }, {});

  return (
    <Wrapper>
      {Object.keys(groupedDocuments).map((key, groupIndex) => {
        const value = groupedDocuments[key];
        return (
          <DocumentGroup key={`group-${key}-${groupIndex}`}>
            {value[0].heading || value[0].subHeading ? (
              <SectionHeaderContainer className="document-group-heading">
                <Title className="section-heading">{value[0].heading}</Title>
                <SubTitle dangerouslySetInnerHTML={{__html: value[0].subHeading}} />
              </SectionHeaderContainer>
            ) : null}

            <CardContent>
              {value[0]?.selectedOption && (
                <InfoText className="body-regular">{parse(value[0].selectedOption.text)}</InfoText>
              )}
              {value[0].documentOptions?.length > 0 && (
                <Dropdown
                  options={getOptions(value[0].documentOptions, value[0].onChangeDocOption)}
                  title={
                    value[0].selectedOption?.render
                      ? parse(value[0].selectedOption?.render)
                      : 'Choose an option'
                  }
                  size="small"
                />
              )}

              <DocumentsContainer>
                {value?.map((item, itemIndex) => (
                  <DocumentItem key={`${item.name}-${itemIndex}`}>
                    <DropzoneLabel dangerouslySetInnerHTML={{__html: item?.uploadMessage}} />
                    <Dropzone
                      onDrop={(file) => item?.onDrop(file, item.name)}
                      multiple={item.multiple}
                      accept={acceptFiles}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                    >
                      {({getRootProps, getInputProps, open}) => (
                        <DropZoneContainer
                          {...getRootProps()}
                          isDragging={isDragging}
                          hasFiles={uploadedFiles.some((file) => file.id === item.name)}
                        >
                          <FileInput {...getInputProps()} />

                          {!uploadedFiles.some((file) => file.id === item.name) ? (
                            <DropzoneContent>
                              <MainUploadSection>
                                <UploadIconContainer>
                                  <UploadIcon src={IMAGES.fileUploadImage} alt="Upload" />
                                </UploadIconContainer>
                                <TextContainer>
                                  <UploadDescription className="body-small">
                                    Drag 'n' drop the document here or
                                  </UploadDescription>
                                  <UploadButton className="body-small">
                                    Click to select files
                                  </UploadButton>
                                </TextContainer>
                              </MainUploadSection>
                            </DropzoneContent>
                          ) : (
                            uploadedFiles
                              .filter((file) => file.id === item.name)
                              .map((eachFile) => (
                                <FilePreview key={eachFile.id || eachFile.name}>
                                  <FileHeader>
                                    <FileIcon src={IMAGES.fileIcon} alt="File" />
                                    <FileName className="body-medium">{eachFile.name}</FileName>
                                  </FileHeader>
                                  <FileInfo>
                                    <FileSize className="body-small">
                                      {(eachFile.size * 0.001).toFixed(2)} KB
                                    </FileSize>
                                  </FileInfo>
                                  <FileActions>
                                    <FileButton
                                      className="body-small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        open();
                                      }}
                                    >
                                      Change File
                                    </FileButton>
                                    <FileButton
                                      className="body-small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteUploadedFile(eachFile);
                                      }}
                                    >
                                      Remove
                                    </FileButton>
                                  </FileActions>
                                </FilePreview>
                              ))
                          )}
                        </DropZoneContainer>
                      )}
                    </Dropzone>
                  </DocumentItem>
                ))}
              </DocumentsContainer>
              <SupportedFormats className="body-small">
                Supported file formats include only png, jpg, jpeg and pdf
              </SupportedFormats>
            </CardContent>
          </DocumentGroup>
        );
      })}

      {commonInstructions && (
        <AttentionContainer>
          <AttentionTitle className="body-regular font-bold">Attention:</AttentionTitle>
          <AttentionContent
            className="body-regular"
            dangerouslySetInnerHTML={{__html: commonInstructions}}
          />
        </AttentionContainer>
      )}

      <CardFooter>
        <ActionButton
          buttonText={actionButton?.buttonText}
          callback={actionButton?.callback}
          isLoading={actionButton?.isLoading}
          isDisabled={actionButton?.isDisabled}
          className="my-2"
        />
      </CardFooter>
    </Wrapper>
  );
};

UploadMultipleDocuments.propTypes = propTypes;

export default UploadMultipleDocuments;

const Wrapper = styled.div`
  margin: 0 auto;
  height: 100%;

  .warning-text {
    color: ${(props) => props.theme.colors.feedback.warning} !important;
    font-weight: ${(props) => props.theme.typography.fontWeight.bold};
  }

  .document-group-heading {
    border-bottom: none;
    border-top: 1px solid ${(props) => props.theme.colors.border.default} !important;
    padding-top: 2rem !important;
  }
`;

const DocumentGroup = styled.div`
  margin-bottom: ${(props) => props.theme.spacing.lg};
`;

const Title = styled.h1`
  margin: 0;
  padding: 0;
  color: ${(props) => props.theme.colors.text.primary};
`;

const SubTitle = styled.p`
  margin: ${(props) => props.theme.spacing.xs} 0 0;
  padding: 0;
  color: ${(props) => props.theme.colors.text.secondary};
`;

const CardContent = styled.div`
  padding: 0 ${(props) => props.theme.spacing.lg};
`;

const CardFooter = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 ${(props) => props.theme.spacing.lg};
`;

const InfoText = styled.p`
  color: ${(props) => props.theme.colors.brand.primary};
  margin: ${(props) => props.theme.spacing.md} 0;
`;

const DocumentsContainer = styled.div`
  display: flex;
  gap: ${(props) => props.theme.spacing.lg};
  margin-top: ${(props) => props.theme.spacing.md};
  flex-direction: column;

  @media (min-width: ${(props) => props.theme.breakpoints.md}) {
    flex-direction: row;
  }
`;

const DocumentItem = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const DropzoneLabel = styled.div`
  margin-bottom: ${(props) => props.theme.spacing.xs};
  color: ${(props) => props.theme.colors.text.secondary};

  b {
    color: ${(props) => props.theme.colors.text.primary};
  }
`;

const DropZoneContainer = styled.div`
  border: 2px dashed ${(props) => (props.isDragging ? props.theme.colors.brand.primary : '#ddc9f4')};
  border-radius: 0.8rem;
  background-color: #ffffff;
  padding: 2.4rem;
  transition: all 0.2s ease-in-out;
  cursor: pointer;
  margin: 1.6rem 0 0 0;
  background-color: ${(props) => (props.hasFiles ? '#F8F2FF' : '#ffffff')};
  flex-grow: 1;

  &:hover {
    border-color: #a6a6a6b5;
  }
  cursor: pointer;
  margin-top: ${(props) => props.theme.spacing.xs};

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    padding: 2rem 1rem;
  }
`;

const DropzoneContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: ${(props) => props.theme.spacing.md};
`;

const MainUploadSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing.md};
`;

const TextContainer = styled.div`
  text-align: left;

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    text-align: center;
  }
`;

const UploadIconContainer = styled.div`
  width: 6.4rem;
  height: 6.4rem;
  flex-shrink: 0;
  border-radius: 50%;
  background-color: ${(props) => props.theme.colors.brand.secondary};
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    width: 4.8rem;
    height: 4.8rem;
  }
`;

const UploadIcon = styled.img`
  width: 3.2rem;
  height: 3.2rem;

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    width: 2.4rem;
    height: 2.4rem;
  }
`;

const UploadButton = styled.button`
  background-color: ${(props) => props.theme.colors.background.primary};
  border: 1px solid ${(props) => props.theme.colors.brand.tertiary};
  border-radius: ${(props) => props.theme.borderRadius.sm};
  padding: ${(props) => props.theme.spacing.xs} ${(props) => props.theme.spacing.md};
  cursor: pointer;
  margin-top: ${(props) => props.theme.spacing.sm};
  display: inline-block;
  text-align: center;

  &:hover {
    background-color: #f8fafc;
  }
`;

const UploadDescription = styled.p`
  color: ${(props) => props.theme.colors.text.secondary};
  margin: 0;
`;

const FilePreview = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const FileHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing.sm};
  margin-bottom: ${(props) => props.theme.spacing.sm};
`;

const FileIcon = styled.img`
  width: 2.4rem;
  height: 2.4rem;
`;

const FileName = styled.span`
  color: ${(props) => props.theme.colors.text.primary};
  word-break: break-all;
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;
  flex: 1;
  text-align: left;
`;

const FileInfo = styled.div`
  margin-bottom: ${(props) => props.theme.spacing.sm};
`;

const FileSize = styled.span`
  color: ${(props) => props.theme.colors.text.secondary};
`;

const FileActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${(props) => props.theme.spacing.sm};
`;

const FileButton = styled.button`
  background-color: ${(props) => props.theme.colors.background.primary};
  border: 1px solid ${(props) => props.theme.colors.brand.tertiary};
  border-radius: ${(props) => props.theme.borderRadius.sm};
  padding: ${(props) => props.theme.spacing.xs} ${(props) => props.theme.spacing.md};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing.xs};

  &:hover {
    background-color: ${(props) => props.theme.colors.background.secondary};
  }
`;

const SupportedFormats = styled.p`
  color: ${(props) => props.theme.colors.text.secondary};
  margin: ${(props) => props.theme.spacing.xs} 0 0 0;
`;

const AttentionContainer = styled.div`
  margin: ${(props) => props.theme.spacing.lg};
  padding: ${(props) => props.theme.spacing.md};
  background-color: ${(props) => props.theme.colors.background.secondary};
  border-radius: ${(props) => props.theme.borderRadius.md};
  border: 1px solid ${(props) => props.theme.colors.brand.tertiary};
  a {
    color: ${(props) => props.theme.colors.brand.primary};
    font-weight: ${(props) => props.theme.typography.fontWeight.bold};
  }
`;

const AttentionTitle = styled.span`
  color: ${(props) => props.theme.colors.brand.tertiary};
  display: inline-block;
  margin-bottom: ${(props) => props.theme.spacing.xs};
`;

const AttentionContent = styled.div`
  color: ${(props) => props.theme.colors.text.secondary};

  a {
    color: ${(props) => props.theme.colors.brand.primary};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const EachOption = styled.li`
  text-decoration: none;
  padding: 0.7rem;
  display: flex;
  align-items: center;
  color: ${(props) => props.theme.colors.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  font-size: ${(props) => props.theme.typography.fontSize.sm};
  line-height: 1.5;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${(props) => props.theme.colors.background.secondary};
    color: ${(props) => props.theme.colors.brand.primary};
  }

  /* Style any nested HTML from the parsed content */
  p,
  span {
    margin: 0;
    padding: 0;
  }

  b,
  strong {
    color: ${(props) => props.theme.colors.text.primary};
    font-weight: ${(props) => props.theme.typography.fontWeight.medium};
  }
`;

const FileInput = styled.input`
  display: none;
`;
